import type { DataFlow, DataFlowStep, RawGetDataFlow, RawStep } from "./types";

function upsert(records: Map<string, DataFlowStep>, record: DataFlowStep): void {
  const existing = records.get(record.name);
  if (!existing) {
    records.set(record.name, { ...record, inputs: [...record.inputs], outputs: [...record.outputs] });
    return;
  }
  for (const inp of record.inputs) {
    if (!existing.inputs.includes(inp)) existing.inputs.push(inp);
  }
  for (const out of record.outputs) {
    if (!existing.outputs.includes(out)) existing.outputs.push(out);
  }
}

function getOrCreate(name: string, rawByName: Map<string, RawStep>, records: Map<string, DataFlowStep>): DataFlowStep {
  const existing = records.get(name);
  if (existing) return existing;
  const raw = rawByName.get(name);
  return {
    kind: name.startsWith("download") ? "Source" : "Sink",
    name,
    declarationName: raw?.declarationName ?? name,
    project: raw?.project ?? "",
    line: raw?.line ?? 0,
    inputs: [],
    outputs: [],
  };
}

export function transformDataFlow(raw: RawGetDataFlow): DataFlow {
  const { scriptbookId, scriptbookDescription, steps } = raw;
  const rawByName = new Map<string, RawStep>(steps.map((s) => [s.name, s]));
  const records = new Map<string, DataFlowStep>();

  const downloadDestTables = new Set<string>();
  for (const step of steps) {
    if (step.name.startsWith("download")) {
      for (const destTable of step.destTables) {
        downloadDestTables.add(destTable);
      }
    }
  }

  for (const step of steps) {
    const inputs: string[] = [];
    const outputs: string[] = [];

    for (const sourceTable of step.sourceTables) {
      inputs.push(sourceTable);
      upsert(records, {
        kind: "Table",
        name: sourceTable,
        declarationName: sourceTable,
        project: step.project,
        line: step.line,
        inputs: [],
        outputs: [step.name],
      });
    }

    for (const destTable of step.destTables) {
      outputs.push(destTable);
      upsert(records, {
        kind: "Table",
        name: destTable,
        declarationName: destTable,
        project: step.project,
        line: step.line,
        inputs: [step.name],
        outputs: [],
      });
    }

    for (const inputName of step.inputs) {
      inputs.push(inputName);
      const inputRecord = getOrCreate(inputName, rawByName, records);
      if (!inputRecord.outputs.includes(step.name)) inputRecord.outputs.push(step.name);
      upsert(records, inputRecord);
    }

    for (const outputName of step.outputs) {
      outputs.push(outputName);
      const outputRecord = getOrCreate(outputName, rawByName, records);
      if (!outputRecord.inputs.includes(step.name)) outputRecord.inputs.push(step.name);
      upsert(records, outputRecord);
    }

    upsert(records, {
      kind: step.name.startsWith("download") ? "Source" : "Sink",
      name: step.name,
      declarationName: step.declarationName,
      project: step.project,
      line: step.line,
      inputs,
      outputs,
      stepKind: step.kind,
      hasDestTables: step.destTables.length > 0,
    });
  }

  for (const [name, record] of records) {
    if (downloadDestTables.has(name)) {
      record.isDestTable = true;
    }
    // Bundle tables arrive named "download <bundle>.<table>"
    const rawKind = rawByName.get(name)?.kind;
    if (rawKind === "BUNDLE_TABLE" || rawKind === "bundle-table") {
      record.kind = "BundleTable";
      record.stepKind = rawKind;
    }
  }

  return {
    scriptbookId,
    scriptbookDescription,
    steps: Array.from(records.values()),
  };
}

export function filterToNode(nodeName: string, dataFlow: DataFlow): DataFlow {
  const stepMap = new Map(dataFlow.steps.map((s) => [s.name, s]));
  const included = new Set<string>();

  const backQueue = [nodeName];
  for (;;) {
    const name = backQueue.shift();
    if (name === undefined) break;
    if (included.has(name)) continue;
    included.add(name);
    const step = stepMap.get(name);
    if (step) {
      for (const input of step.inputs) {
        if (!included.has(input)) backQueue.push(input);
      }
    }
  }

  const fwdVisited = new Set<string>();
  const fwdQueue = [nodeName];
  for (;;) {
    const name = fwdQueue.shift();
    if (name === undefined) break;
    if (fwdVisited.has(name)) continue;
    fwdVisited.add(name);
    included.add(name);
    const step = stepMap.get(name);
    if (step) {
      for (const output of step.outputs) {
        if (!fwdVisited.has(output)) fwdQueue.push(output);
      }
    }
  }

  return { ...dataFlow, steps: dataFlow.steps.filter((s) => included.has(s.name)) };
}
